<script setup lang="tsx">
import type { OrdenationDropdownOption } from '~/components/Filters/OrdenationSelect';
import type PropertyListSection from '~/components/PropertyList/PropertyListSection.vue';
import type { RegionInfoLinkClickedEmitProps } from '~/components/RegionInfo';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { MenuFixedLevels } from '~/composables/useDragToBottom/types';
import type { PropertiesListingMapState } from '~/composables/useMapListing';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { BaseClusterData } from '~/core/service/googleMaps/components/GmapClusterMarker';
import type { CurrentPage, FilterApplied } from '~/types/mixpanel';
import type { H3ClusterId } from '~~/core/ports/repositories';
import type { BreadcrumbList, WithContext } from 'schema-dts';

import { until } from '@vueuse/core';
import { useEventDataFilters } from '~/composables/events/useEventDataFilters/useEventDataFilters';
import { useEvents } from '~/composables/events/useEvents';
import { usePropertiesListingJsonLd } from '~/composables/jsonLd/usePropertiesListingJsonLd';
import { HEADER_OFFSET_CONFIG } from '~/composables/useDragToBottom/constants';
import { useDragToBottom } from '~/composables/useDragToBottom/index';
import { DEFAULT_FILTER } from '~/composables/useListing/filter';
import { useManager } from '~/composables/useListing/manager';
import { DEFAULT_SERVER_PAGINATION } from '~/composables/useListing/pagination';
import { useListingInfoLabel } from '~/composables/useListingInfoLabel/useListingInfoLabel';
import { useMapClusters } from '~/composables/useMapClusters/useMapClusters';
import {
	MapState,
	useMapController
} from '~/composables/useMapController/useMapController';
import { initializeMapState } from '~/composables/useMapListing';
import { usePropertyResponsiveFilters } from '~/composables/usePropertyResponsiveFilters/usePropertyResponsiveFilters';
import { useSeoTagsListing } from '~/composables/useSeoTagsListing/useSeoTagsListing';
import { useSingleFieldSuggest } from '~/composables/useSingleFieldSuggest';
import {
	PropertyTypePlurals,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { EventBusKeys } from '~/core/helpers/symbols';
import GmapClusterMarker from '~/core/service/googleMaps/components/GmapClusterMarker/GmapClusterMarker.vue';
import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import GmapMarker from '~/core/service/googleMaps/components/GmapMarker/GmapMarker.vue';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import {
	dehyphenateString,
	getChangedKeysFilters,
	grammarInflection,
	slugify
} from '~~/utils';
import { joinURL } from 'ufo';

import MobileScrollToMapButton from '~/components/Buttons/MobileScrollToMapButton.vue';

const { searchPropertyEvents, trackFilterApplied } = useEvents();
const { getFilterAppliedValues } = useEventDataFilters();

definePageMeta({
	name: 'PropertiesListing',
	layout: 'properties-listing',
	pageTitle: 'Imóveis',
	key: 'properties-listing-page',
	alias: ['/venda/imoveis', '/aluguel/imoveis']
});

const route = useRoute();
const router = useRouter();

const mapState = useState<PropertiesListingMapState>(
	'properties-listing-map',
	initializeMapState
);

const gmapRef = ref<InstanceType<typeof GmapMap> | null>(null);
const clusterMarkersRefs = ref<
	Map<string, InstanceType<typeof GmapClusterMarker>>
>(new Map());

const mapOptions = computed(() => {
	return {
		gestureHandling: 'greedy',
		draggable: true,
		scrollwheel: true,
		disableDefaultUI: false,
		zoomControl: true
	};
});

const shouldShowMap = computed(() => {
	return !mapController.state.isInitializing.value;
});

// NOTA: useAddresses() era chamado aqui para pré-carregar dados,
// mas como RegionSelect está dentro de ClientOnly, não é necessário
// chamar no servidor. Os componentes que usam useAddresses() estão
// dentro de <ClientOnly> e farão a chamada quando necessário.

const propertyListSectionRef = useTemplateRef<InstanceType<
	typeof PropertyListSection
> | null>('propertyListSectionRef');

const initialFilters = computed<Partial<PropertyFilters>>(() =>
	PropertyFiltersPresenter.getFiltersFromPath(
		route.fullPath,
		{ ...DEFAULT_FILTER },
		'imoveis'
	)
);

const {
	filters,
	isFetching,
	properties,
	hasProperties,
	filteredCount,
	hasLessThenTenProperties,
	isFetchingForTheFirstTime,
	nextPage,
	updateFiltersManager,
	clearFilters
} = useManager({
	page: 'imoveis',
	initialFilters,
	includeH3Clusters: true,
	config: {
		shouldScrollToTopOnFiltersChange: true,
		scrollElement: propertyListSectionRef
	}
});

// No SSR, aguarda a primeira requisição terminar antes de renderizar
if (import.meta.server) {
	await until(() => !isFetchingForTheFirstTime.value).toBeTruthy();
}

// Sync macroregion with page filters
// This automatically keeps the macroregion in sync when filters change
useSingleFieldSuggest({ filters });

const { updateSeoMeta, pageTitle, pageDescription } = useSeoTagsListing(
	filters,
	filteredCount,
	properties
);

updateSeoMeta();

const shouldAddNoindex = computed<boolean>(
	() => !isFetchingForTheFirstTime.value && !hasProperties.value
);

useHead({
	meta: computed(() =>
		shouldAddNoindex.value
			? [
					{
						name: 'robots',
						content: 'noindex'
					}
				]
			: []
	)
});

const {
	trackMapListingClicked,
	trackBreadcrumbClicked,
	trackCtaViewMoreClicked,
	trackReorderClicked,
	trackEmptyState,
	trackLinkClicked,
	trackMapListingInteracted,
	trackSelectionCleared,
	trackBoundariesCleared
} = useEvents();

const { filterComponents, numberOfAppliedClosedFilters, appliedFilterKeys } =
	usePropertyResponsiveFilters({
		filters: computed(() => filters.value),
		updateFilters: updateFiltersManager,
		pageName: 'imoveis'
	});

function updateOrdenation(event: {
	sortBy: PropertyFilters['sortBy'];
	orderBy: PropertyFilters['orderBy'];
}) {
	router.push(
		PropertyFiltersPresenter.getPathFromFilters(
			{
				...filters.value,
				sortBy: event.sortBy,
				orderBy: event.orderBy
			},
			{},
			{ page: 'imoveis' }
		)
	);
}

async function waitForFilteredCount() {
	return await until(() => typeof filteredCount.value === 'number').toBe(true);
}

async function waitForFetchingToComplete() {
	return await until(() => !isFetching.value).toBe(true);
}

function getLocationChangeType(
	newFilters: PropertyFilters,
	currentFilters: PropertyFilters
): 'addition' | 'removal' | 'substitution' | 'none' {
	const getArrayLength = (arr: any[] | undefined) => arr?.length || 0;

	const current = {
		regions: getArrayLength(currentFilters.regions),
		cities: getArrayLength(currentFilters.cities),
		macroregions: getArrayLength(currentFilters.macroregions),
		hasAddress: !!currentFilters.addressSlug,
		hasPoi: !!(currentFilters.poiLocationLat && currentFilters.poiLocationLng)
	};

	const updated = {
		regions: getArrayLength(newFilters.regions),
		cities: getArrayLength(newFilters.cities),
		macroregions: getArrayLength(newFilters.macroregions),
		hasAddress: !!newFilters.addressSlug,
		hasPoi: !!(newFilters.poiLocationLat && newFilters.poiLocationLng)
	};

	const hasLocationSubstitution =
		(newFilters.regions?.[0] !== currentFilters.regions?.[0] &&
			!!newFilters.regions?.[0]) ||
		(newFilters.cities?.[0] !== currentFilters.cities?.[0] &&
			!!newFilters.cities?.[0]) ||
		(newFilters.states?.[0] !== currentFilters.states?.[0] &&
			!!newFilters.states?.[0]) ||
		(newFilters.macroregions?.[0] !== currentFilters.macroregions?.[0] &&
			!!newFilters.macroregions?.[0]) ||
		(newFilters.addressSlug !== currentFilters.addressSlug &&
			updated.hasAddress) ||
		((newFilters.poiLocationLat !== currentFilters.poiLocationLat ||
			newFilters.poiLocationLng !== currentFilters.poiLocationLng) &&
			updated.hasPoi);

	if (hasLocationSubstitution) return 'substitution';

	const hasLocationAddition =
		updated.regions > current.regions ||
		updated.cities > current.cities ||
		updated.macroregions > current.macroregions ||
		(!current.hasAddress && updated.hasAddress) ||
		(!current.hasPoi && updated.hasPoi);

	if (hasLocationAddition) return 'addition';

	const hasLocationRemoval =
		updated.regions < current.regions ||
		updated.cities < current.cities ||
		updated.macroregions < current.macroregions ||
		(current.hasAddress && !updated.hasAddress) ||
		(current.hasPoi && !updated.hasPoi);

	if (hasLocationRemoval) return 'removal';

	return 'none';
}

function createBaseFilters(
	newFilters: PropertyFilters,
	locationChangeType: 'addition' | 'removal' | 'substitution' | 'none'
): PropertyFilters {
	const shouldPreserveBounds = locationChangeType === 'none';
	const hasLocationChange = locationChangeType !== 'none';

	let bounds = undefined;
	let zoom = undefined;
	let h3Ids = newFilters.h3Ids;

	if (shouldPreserveBounds) {
		const currentBounds = gmapRef.value?.mapInstance?.getBounds();
		const currentZoom = gmapRef.value?.mapInstance?.getZoom();

		bounds = currentBounds
			? getBoundsString(currentBounds)
			: filters.value.bounds;
		zoom = currentZoom || filters.value.zoom;
	}

	if (hasLocationChange) {
		h3Ids = undefined;
	}

	return { ...newFilters, bounds, zoom, h3Ids } as PropertyFilters;
}

type LocationChangeType = 'addition' | 'removal' | 'substitution' | 'none';

function updateClustersAndProperties(
	clusterFilters: PropertyFilters,
	propertyFilters: PropertyFilters,
	locationChangeType: LocationChangeType
): void {
	updateClustersForLocationChange(clusterFilters, locationChangeType);
	updateUrlAndListing(propertyFilters);
}

function updateClustersForLocationChange(
	newFilters: PropertyFilters,
	changeType: LocationChangeType
): void {
	try {
		if (changeType === 'removal') {
			const filtersData = {
				...newFilters,
				bounds: undefined,
				zoom: undefined,
				h3Ids: undefined
			};

			mapController.processLocationFiltersChange('filters-bar');
			mapController.forceUpdateClusters(filtersData as PropertyFilters);
		} else if (changeType === 'addition' || changeType === 'substitution') {
			const filtersData = {
				...newFilters,
				bounds: undefined,
				zoom: undefined,
				h3Ids: undefined
			};

			mapController.processLocationFiltersChange('filters-bar');
			mapController.forceUpdateClusters(filtersData as PropertyFilters);
		} else {
			const currentBounds = gmapRef.value?.mapInstance?.getBounds();
			const currentZoom = gmapRef.value?.mapInstance?.getZoom();

			const filtersData = {
				...newFilters,
				bounds: currentBounds
					? getBoundsString(currentBounds)
					: filters.value.bounds,
				zoom: currentZoom || filters.value.zoom
			};

			mapController.updateClusters(filtersData as PropertyFilters);
		}
	} catch (error) {
		throw error;
	}
}

async function applyFiltersFromFiltersBar(newFilters: PropertyFilters) {
	const locationChangeType = getLocationChangeType(newFilters, filters.value);

	const hasLocationChange = locationChangeType !== 'none';
	if (hasLocationChange) {
		clearSelectedClusters();
		await nextTick();
	}

	const baseFilters = createBaseFilters(newFilters, locationChangeType);
	const clusterFilters = { ...baseFilters };
	const propertyFilters = { ...baseFilters };

	updateClustersAndProperties(
		clusterFilters,
		propertyFilters,
		locationChangeType
	);

	return baseFilters;
}

function handleApplyFilter(newFilters: PropertyFilters) {
	applyFiltersFromFiltersBar(newFilters);
}

async function handleSidebarApplyFilter(newFilters: PropertyFilters) {
	const changedKeys = getChangedKeysFilters(newFilters, filters.value);

	applyFiltersFromFiltersBar(newFilters);
	isSidebarActive.value = false;

	if (changedKeys.length === 0) return;
	await until(() => isFetching.value).toBeTruthy();
	await Promise.all([waitForFetchingToComplete(), waitForFilteredCount()]);

	const filterApplied = getFilterAppliedValues(
		newFilters,
		'moreFilters',
		changedKeys
	);
	filterApplied[MixpanelProps.TOTAL_RESULTS] = filteredCount.value;
	searchPropertyEvents.trackFilterApplied(filterApplied);
}

async function handleSidebarClearFilters() {
	clearFilters();
	await nextTick();

	const baseFilters = createBaseFilters(filters.value, 'removal');
	updateClustersForLocationChange(baseFilters, 'removal');

	clearFiltersAndClusters(baseFilters);

	isSidebarActive.value = false;
}

const infoLabel = useListingInfoLabel(() => {
	const { regions, cities, states, addressSlug } = filters.value;

	return {
		regions: regions || [],
		cities: cities || [],
		regionType: filters.value?.macroregions?.[0] || null,
		state: states?.[0] || '',
		addressSlug: addressSlug || ''
	};
});

const getPropertyTypeText = (filters: PropertyFilters, count: number) => {
	if (filters.propertyTypes?.length === 1) {
		const propertyType = filters.propertyTypes[0]!;

		return count > 1
			? PropertyTypePlurals[propertyType]
			: propertyTypesTranslations[propertyType];
	}

	return grammarInflection(count, 'imóvel', 'imóveis');
};
const propertyCountLabel = computed<string>(
	() =>
		`${filteredCount.value} ${getPropertyTypeText(filters.value, filteredCount.value).toLocaleLowerCase()}`
);

const mobilePropertyCountLabel = computed<string>(() => {
	const count = filteredCount.value;
	const article = grammarInflection(count, 'o', 'os');
	const propertyWord = grammarInflection(count, 'imóvel', 'imóveis');
	const countString = filteredCount.value > 1 ? count : '';

	return `Toque ou arraste para ver ${article} ${countString} ${propertyWord}`;
});

const shouldShowNextPageButton = computed<boolean>(() => {
	if (isFetchingForTheFirstTime.value) return false;

	return properties.value.length < filteredCount.value;
});

interface AddressSlugParts {
	visual: {
		locationName: string;
		street: string;
		streetNumber: string;
		neighborhood: string;
		city: string;
		state: string;
	};
	route: {
		locationName: string;
		street: string;
		streetNumber: string;
		neighborhood: string;
		city: string;
		state: string;
	};
}

const addressSlugDetails = computed<AddressSlugParts>(() => {
	if (!filters.value.addressSlug) {
		return {
			visual: {
				locationName: '',
				street: '',
				streetNumber: '',
				neighborhood: '',
				city: '',
				state: ''
			},
			route: {
				locationName: '',
				street: '',
				streetNumber: '',
				neighborhood: '',
				city: '',
				state: ''
			}
		};
	}

	// Split by comma and clean up
	const parts = filters.value.addressSlug.split(',').map(part => part.trim());

	let locationName = '';
	let street = '';
	let streetNumber = '';
	let neighborhood = '';
	let city = '';
	let state = '';

	// Check if it's a POI (has street with hyphen)
	if (parts[1]?.includes('-')) {
		// Format: "Parque Ibirapuera, Avenida Pedro Álvares Cabral - Vila Mariana, São Paulo - SP"
		locationName = parts[0] || '';
		const [streetPart = '', neighborhoodPart = ''] = (parts[1] || '')
			.split('-')
			.map(p => p.trim());
		street = streetPart;
		neighborhood = neighborhoodPart;

		// Handle city and state
		const [cityPart = '', statePart = ''] = (parts[2] || '')
			.split('-')
			.map(p => p.trim());
		city = cityPart;
		state = statePart;
	} else {
		// Check if it's a street with number
		const isStreetWithNumber = parts[1] && /^\d+$/.test(parts[1].trim());

		if (isStreetWithNumber) {
			// Format: "Rua Tabapuã, 25, Itaim Bibi, São Paulo - SP"
			street = parts[0] || '';
			streetNumber = parts[1] || '';
			neighborhood = parts[2] || '';
		} else {
			// Format: "Rua Tabapuã, Itaim Bibi, São Paulo - SP"
			street = parts[0] || '';
			neighborhood = parts[1] || '';
		}

		// In both cases, city and state are handled the same way
		const cityStatePart = parts[parts.length - 1] || '';
		const [cityPart = '', statePart = ''] = cityStatePart
			.split('-')
			.map(p => p.trim());
		city = cityPart;
		state = statePart;

		// For non-POI cases, street is also the location name
		locationName = street;
	}

	const visual = {
		locationName,
		street,
		streetNumber,
		neighborhood,
		city,
		state
	};

	const route = {
		locationName: slugify(visual.locationName),
		street: slugify(visual.street),
		streetNumber: slugify(visual.streetNumber),
		neighborhood: slugify(visual.neighborhood),
		city: slugify(visual.city),
		state: slugify(visual.state)
	};

	return {
		visual,
		route
	};
});

const isPointOfInterest = computed(
	() => filters.value.poiLocationLat && filters.value.poiLocationLng
);

const breadcrumbItems = computed<
	(BreadcrumbItem & { structuredDataLabel?: string })[]
>(() => {
	const state = dehyphenateString(filters.value.states?.[0] || 'SP');
	const city = dehyphenateString(filters.value.cities?.[0] || 'São Paulo');
	const region = dehyphenateString(filters.value.regions?.[0] || '');
	const addressSlug = filters.value.addressSlug?.split(',')[0] ?? '';

	const items: (BreadcrumbItem & { structuredDataLabel?: string })[] = [];

	// Adiciona o item inicial (Início)
	items.push({
		label: 'Início',
		structuredDataLabel: 'PilarHomes',
		route: '/',
		separator: false,
		type: 'home'
	});

	if (filters.value.macroregions?.length) {
		if (region) {
			// Case: macroregion + region + city
			// Shows: city -> region
			items.push({
				label: `${city} - ${state}`,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [state],
					cities: [city]
				}),
				separator: true,
				type: 'city'
			});
			items.push({
				label: region,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					macroregions: [filters.value.macroregions[0]!],
					states: [state],
					cities: [city],
					regions: [region]
				}),
				separator: true,
				type: 'region'
			});
		} else if (filters.value.cities?.length) {
			// Case: macroregion + city (no region)
			// Shows: city
			items.push({
				label: `${city} - ${state}`,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [state],
					cities: [city]
				}),
				separator: true,
				type: 'city'
			});
		} else {
			// Case: macroregion only (no city, no region)
			// Shows: macroregion
			items.push({
				label: `${filters.value.macroregions[0]!} - ${state}`,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					macroregions: [filters.value.macroregions[0]!],
					states: [state]
				}),
				separator: true,
				type: 'macroregion'
			});
		}

		return items;
	}
	if (state && city) {
		items.push({
			label: `${city} - ${state}`,
			route: PropertyFiltersPresenter.getRouteObjectFromFilters({
				states: [state],
				cities: [city]
			}),
			separator: true,
			type: 'city'
		});
	}
	if (isPointOfInterest.value) {
		const { route, visual } = addressSlugDetails.value;

		if (visual.neighborhood) {
			items.push({
				label: visual.neighborhood,
				separator: true,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [route.state],
					cities: [route.city],
					regions: [visual.neighborhood]
				}),
				type: 'region'
			});
		}
		if (visual.locationName) {
			items.push({
				label: visual.locationName,
				separator: true,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters(
					{
						states: [route.state],
						addressSlug: `${addressSlug}, ${visual.neighborhood}, ${visual.city} - ${visual.state}`,
						cities: [route.city],
						poiLocationLat: filters.value.poiLocationLat,
						poiLocationLng: filters.value.poiLocationLng
					},
					{
						page: 'imoveis'
					}
				),
				type: 'street'
			});
		}
		if (visual.streetNumber) {
			items.push({
				label: visual.streetNumber,
				separator: false,
				route: PropertyFiltersPresenter.getRouteObjectFromFilters({
					states: [route.state],
					addressSlug: filters.value.addressSlug,
					locationSlug: filters.value.locationSlug,
					poiLocationLat: filters.value.poiLocationLat,
					poiLocationLng: filters.value.poiLocationLng
				}),
				type: 'street'
			});
		}

		return items || [];
	}
	if (region) {
		items.push({
			label: region,
			separator: true,
			route: PropertyFiltersPresenter.getRouteObjectFromFilters({
				states: [state],
				cities: [city],
				regions: [region]
			}),
			type: 'region'
		});
	}

	if (addressSlug) {
		items.push({
			label: addressSlug,
			separator: true,
			route: PropertyFiltersPresenter.getRouteObjectFromFilters({
				states: [state],
				cities: [city],
				regions: region ? [region] : undefined,
				addressSlug
			}),
			type: 'region'
		});
	}

	return items;
});

const websiteUrl = useRuntimeConfig().public.ORIGIN;

const breadcrumbListJsonLd = computed<WithContext<BreadcrumbList> | null>(() =>
	breadcrumbItems.value.length
		? ({
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: breadcrumbItems.value.map((item, index) => {
					const path: string =
						typeof item.route === 'string' ? item.route : item.route.path!;

					return {
						'@type': 'ListItem',
						position: index + 1,
						name: item.label,
						item: joinURL(websiteUrl, path)
					};
				})
			} satisfies WithContext<BreadcrumbList>)
		: null
);

useJsonld(() => breadcrumbListJsonLd.value);

const emptyStateData = computed<{ title: string; description: string } | null>(
	() => {
		if (isFetching.value) return null;

		if (hasProperties.value && hasLessThenTenProperties.value) {
			return {
				title: 'Esses foram os imóveis encontrados',
				description: `Ajuste os filtros para ver mais imóveis ${infoLabel.value}` //
			};
		}

		if (!isFetching.value && !hasProperties.value) {
			return {
				title: 'Nenhum imóvel encontrado',
				description: `Ajuste os filtros para ver imóveis ${infoLabel.value}`
			};
		}

		return null;
	}
);

whenever(
	() => emptyStateData.value,
	() => {
		trackEmptyState({
			filters: filters.value,
			currentPage: 'Listing Properties',
			propertiesCount: properties.value.length
		});
	}
);

const isSidebarActive = shallowRef<boolean>(false);
const bodyElement = ref<HTMLElement | null>(null);
const isBodyScrollLocked = useScrollLock(bodyElement);

watch(
	isSidebarActive,
	shouldDisableBodyScroll =>
		(isBodyScrollLocked.value = shouldDisableBodyScroll ? true : false)
);

function handleOpenMapVisionButtonClick(clickLocation: 'cta' | 'map') {
	trackMapListingClicked({
		current_page: 'listed properties',
		click_location: clickLocation,
		cta_text: 'Ver imóveis no Mapa',
		destination_url: PropertyFiltersPresenter.getPathFromFilters(
			filters.value,
			{},
			{ page: 'mapa-de-imoveis' }
		),
		property_count: filteredCount.value,
		filter_city: filters.value.cities?.[0],
		filter_region: filters.value.macroregions?.[0],
		filter_neighborhood: filters.value.regions?.[0]
	});
}

function handleBreadcrumbClicked(breadcrumbItem: BreadcrumbItemWithIndex) {
	const appliedFilters = PropertyFiltersPresenter.getFiltersFromPath(
		(breadcrumbItem.route as Record<string, string>).path!,
		filters.value
	);

	if (appliedFilters.addressSlug) {
		let city = '';
		let state = '';

		// Verifica se o addressSlug tem vírgulas (formato antigo)
		if (appliedFilters.addressSlug.includes(',')) {
			const [, parsedCity, parsedState] = appliedFilters.addressSlug.split(',');
			city = parsedCity?.trim() || '';
			state = parsedState?.trim() || '';
		} else {
			// Para URLs como "batel-curitiba-pr", extrai cidade e estado do slug
			const slugParts = appliedFilters.addressSlug.split('-');
			if (slugParts.length >= 3) {
				// Assume que os últimos dois elementos são cidade-estado
				city = slugParts[slugParts.length - 2] || '';
				state = slugParts[slugParts.length - 1] || '';
			}
		}

		if (city && state) {
			const slugifiedAddress = slugify(`${city}, ${state}`);
			const locationSlug = `${slugifiedAddress}-brasil`;
			appliedFilters.locationSlug = locationSlug;
		}
	}

	if (breadcrumbItem.type === 'macroregion') {
		const macroregion = (breadcrumbItem.route as any).query.macroregions;

		appliedFilters.macroregions = [macroregion];
	}

	if (breadcrumbItem.route !== '/') {
		applyFiltersFromFiltersBar(appliedFilters);
	}

	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.CLICK_HOME]: breadcrumbItem.type === 'home',
		[MixpanelProps.CLICK_CITY]: breadcrumbItem.type === 'city',
		[MixpanelProps.CLICK_NEIGHBORHOOD]: breadcrumbItem.type === 'region',
		[MixpanelProps.CLICK_MACROREGION]: breadcrumbItem.type === 'macroregion',
		[MixpanelProps.CLICK_STREET]: breadcrumbItem.type === 'street',
		[MixpanelProps.CLICK_STATE]: breadcrumbItem.type === 'state',
		[MixpanelProps.CTA_TEXT]: breadcrumbItem.label,
		[MixpanelProps.DESTINATION_URL]:
			(breadcrumbItem.route as Record<string, string>).path || '/'
	});
}
const clickCount = ref(0);

function handleViewMoreClicked(page: string) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: page,
		[MixpanelProps.DESTINATION_URL]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: 'Ver mais',
		[MixpanelProps.CLICK_COUNT]: clickCount.value++
	});
}

const eventCleanupFunctions: (() => void)[] = [];

const propertyCardHoverBus = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardHover
);
const propertyCardUnhoverBus = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardUnhover
);
const viewMoreBus = useEventBus(EventBusKeys.viewMoreClicked);

eventCleanupFunctions.push(
	propertyCardHoverBus.on(handleCardHover),
	propertyCardUnhoverBus.on(handleCardUnhover),
	viewMoreBus.on(handleViewMoreClicked)
);

function handleReorderClicked(value: OrdenationDropdownOption['value']) {
	moveBackToInitialPosition();
	trackReorderClicked({
		[MixpanelProps.CLICK_EXCLUSIVES]: value === 'pilar_exclusivities',
		[MixpanelProps.CLICK_HIGHLIGHTS]: value === 'highlights',
		[MixpanelProps.CLICK_LOWEST_PRICE]: value === 'asking_price_asc',
		[MixpanelProps.CLICK_HIGHEST_PRICE]: value === 'asking_price_desc',
		[MixpanelProps.CLICK_FEWER_PHOTOS]: value === 'images_count_asc',
		[MixpanelProps.CLICK_MORE_PHOTOS]: value === 'images_count_desc',
		[MixpanelProps.CLICK_MORE_RELEVANT]: value === 'default_ordenation',
		[MixpanelProps.CLICK_MORE_RECENT]: value === 'wishlist_recent',
		[MixpanelProps.CLICK_OLDEST]: value === 'wishlist_oldest'
	});
}

function handleSearchByCodeEvent(event: FilterApplied) {
	trackFilterApplied(event);
}

function handleRegionClicked({
	ctaText,
	destinationUrl
}: RegionInfoLinkClickedEmitProps) {
	trackLinkClicked({
		[MixpanelProps.CURRENT_PAGE]: 'listed properties',
		[MixpanelProps.SECTION_NAME]: 'neighborhood information',
		[MixpanelProps.LINK_TEXT]: ctaText,
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.LINK_CATEGORY]: 'Informações do bairro'
	});
}

/**
 * O número de imóveis no JSON-LD é limitado a 10 pois não vamos exibir mais do que isso na primeira página,
 * que é o que vem renderizado no servidor. Além disso, se atualizarmos o JSON-LD toda vez que mais imóveis
 * forem carregados no cliente, pode ser que impacte a performance da página.
 **/
usePropertiesListingJsonLd(
	() => properties.value.slice(0, DEFAULT_SERVER_PAGINATION.page),
	{
		pageTitle: () => pageTitle.value,
		pageDescription: () => pageDescription.value
	}
);

const addressGmapMarker = computed(() => {
	if (!filters.value.addressSlug) return '';

	const [firstPart, secondPart] = filters.value.addressSlug.split(',');

	if (!secondPart?.trim()) return firstPart;

	const hasNumberAfterComma = /^\s*\d/.test(secondPart);

	return hasNumberAfterComma
		? `${firstPart},${secondPart.split(',')[0]}`
		: firstPart;
});

const markerPosition = computed<{ lat: number; lng: number } | null>(() => {
	if (!filters.value.poiLocationLat || !filters.value.poiLocationLng) {
		return null;
	}

	return {
		lat: filters.value.poiLocationLat,
		lng: filters.value.poiLocationLng
	};
});

function handleSearchByCondoEvent(event: FilterApplied) {
	trackFilterApplied(event);
}

async function handleOpenMapFromMobileButton() {
	await propertyListSectionRef.value?.scrollToTop();
	toggleToClosedWhenOpen();
	handleOpenMapVisionButtonClick('cta');
}

const menuFixedLevels = computed<MenuFixedLevels>(() => {
	const isEmptyStateWithNoProperties =
		emptyStateData.value && properties.value.length === 0;

	return {
		1: isEmptyStateWithNoProperties ? 400 : 220,
		2: 122
	};
});

const {
	dragRef,
	shouldFixOnBottom,
	isDragging,
	currentY,
	menuBottomLevel,
	moveBackOneLevel,
	moveBackToInitialPosition,
	toggleToClosedWhenOpen,
	toggleToHalfOpenWhenClosed,
	isTransitioning
} = useDragToBottom({
	menuFixedLevels,
	scrollContainer: propertyListSectionRef
});

const isMenuOpen = computed(() => [0, 1].includes(menuBottomLevel.value));
const shouldShowMobilePropertyCountLabel = computed<boolean>(() => {
	return !!(menuBottomLevel.value === 2 && filteredCount.value);
});

function selectClustersFromH3Ids(): void {
	if (!filters.value.h3Ids?.length || !clusters.value.length) {
		return;
	}

	const h3IdsSet = new Set(filters.value.h3Ids);

	clusters.value.forEach(cluster => {
		const clusterId = cluster.geometry.id;

		if (h3IdsSet.has(clusterId) && !selectedClustersData.value.has(clusterId)) {
			originalHandleClusterSelectionChanged({
				clusterId: cluster.geometry.id,
				isSelected: true
			});
		}
	});
}

onMounted(() => {
	bodyElement.value = document.body;
	mapController.handleInitialMapLoad();
});

const {
	clusters,
	selectedClustersData,
	selectedH3Ids,
	markersKey,
	updateClusters: originalUpdateClusters,
	handleClusterSelectionChanged: originalHandleClusterSelectionChanged,
	handleClusterDeselectedByResolution:
		originalHandleClusterDeselectedByResolution,
	normalizeClusterCoordinates,
	clearSelectedClusters
} = useMapClusters({
	gmapRef
});

// Reactive wrapper that only returns clusters when map instance is available
const readyClusters = computed(() => {
	const mapInstance = gmapRef.value?.mapInstance;
	const currentClusters = clusters.value;

	if (!mapInstance) {
		return [];
	}

	return currentClusters;
});

const mapController = useMapController({
	filters,
	clusters,
	gmapRef,
	selectedClustersData,
	normalizeClusterCoordinates,
	originalUpdateClusters,
	pageConfig: {
		page: 'imoveis'
	}
});

function getClusterKey(clusterId: string): string {
	const isSelected = selectedClustersData.value.has(clusterId);

	if (isSelected) {
		return `selected-${clusterId}`;
	} else {
		return `unselected-${markersKey.value}-${clusterId}`;
	}
}

function updateUrlForH3Ids(newH3Ids: string[]): void {
	const newFilters = { ...filters.value, h3Ids: newH3Ids };
	const mapInstance = gmapRef.value?.mapInstance;
	const currentZoom = mapInstance?.getZoom();

	if (!newH3Ids?.length) {
		const currentBounds = mapInstance?.getBounds();

		if (currentBounds) {
			newFilters.bounds = getBoundsString(currentBounds);
		}
	}

	newFilters.zoom = currentZoom;

	const regionsFilters = {
		regions: newFilters.regions,
		cities: newFilters.cities,
		states: newFilters.states,
		macroregions: newFilters.macroregions
	};

	router.push(
		PropertyFiltersPresenter.getPathFromFilters(newFilters, regionsFilters, {
			page: 'imoveis'
		})
	);
}

watch(selectedH3Ids, (newH3Ids, oldH3Ids) => {
	if (mapController.state.currentState.value !== MapState.READY) return;

	const hasChanges =
		JSON.stringify(newH3Ids) !== JSON.stringify(filters.value.h3Ids);

	if (hasChanges) {
		updateUrlForH3Ids(newH3Ids ?? []);

		const hasNewSelection = (newH3Ids?.length ?? 0) > (oldH3Ids?.length || 0);

		if (hasNewSelection) {
			toggleToHalfOpenWhenClosed();
		}
	}
});

function handleDragEnd(): void {
	mapController.handleDragEnd();
}

function handleZoomChanged(zoom: number | undefined): void {
	if (!gmapRef.value?.mapInstance || !zoom) return;
	mapController.handleZoomChanged(zoom);
}

function getBoundsString(bounds: google.maps.LatLngBounds): string {
	const { north, south, east, west } = bounds.toJSON();
	return `${west},${south},${east},${north}`;
}

function updateUrlAndListing(filtersData: PropertyFilters) {
	const regionsFilters = {
		regions: filtersData.regions,
		cities: filtersData.cities,
		states: filtersData.states,
		macroregions: filtersData.macroregions
	};
	router.push(
		PropertyFiltersPresenter.getPathFromFilters(filtersData, regionsFilters, {
			page: 'imoveis'
		})
	);
}

function clearFiltersAndClusters(
	newFilters: PropertyFilters,
	regionsFilters?: {
		regions?: string[];
		cities?: string[];
		states?: string[];
		macroregions?: string[];
	}
) {
	const hadSelectedClusters = selectedClustersData.value.size > 0;
	if (hadSelectedClusters) {
		clearSelectedClusters();
	}

	const finalFilters = { ...newFilters, h3Ids: [] };

	router.push(
		PropertyFiltersPresenter.getPathFromFilters(
			finalFilters,
			regionsFilters || {},
			{
				page: 'imoveis'
			}
		)
	);
}

watch(
	() => markersKey.value,
	() => {
		const selectedClusterIds = new Set(selectedClustersData.value.keys());

		const clustersToDelete = Array.from(clusterMarkersRefs.value.keys()).filter(
			clusterId => !selectedClusterIds.has(clusterId)
		);

		clustersToDelete.forEach(clusterId => {
			clusterMarkersRefs.value.delete(clusterId);
		});
	}
);

watchOnce(clusters as any, (newClusters: any, oldClusters: any) => {
	const shouldAutoSelect =
		filters.value.h3Ids?.length &&
		newClusters.length > 0 &&
		(!oldClusters || oldClusters.length === 0);

	if (shouldAutoSelect) {
		const mapInstance = gmapRef.value?.mapInstance;

		if (mapInstance) {
			selectClustersFromH3Ids();
			const idleListener = mapInstance.addListener('idle', () => {
				google.maps.event.removeListener(idleListener);

				mapController.clearProgrammaticFlag();
			});
		}
	}
});

onUnmounted(() => {
	eventCleanupFunctions.forEach(cleanup => cleanup());
	eventCleanupFunctions.length = 0;
	clusterMarkersRefs.value.clear();
});

const shouldShowSeeAllCityButton = computed(() => {
	return !!(
		filters.value.regions?.length ||
		filters.value.addressSlug ||
		(filters.value.poiLocationLat && filters.value.poiLocationLng)
	);
});

function handleSeeAllCity() {
	trackBoundariesCleared({
		current_page: 'Listing Map',
		property_count: totalSelectedPropertiesCount.value,
		neighborhoods_name: filters.value.regions?.join(', ') || '',
		square_count: selectedClustersData.value.size
	});

	const newFilters = { ...filters.value };

	let cities = newFilters.cities || [];
	let states = newFilters.states || [];

	if (newFilters.addressSlug && (!cities.length || !states.length)) {
		const { city, state } = addressSlugDetails.value.visual;
		if (city && !cities.length) {
			cities = [city];
		}
		if (state && !states.length) {
			states = [state];
		}
	}

	newFilters.cities = cities;
	newFilters.states = states;

	newFilters.regions = [];
	newFilters.addressSlug = '';
	newFilters.poiLocationLat = undefined;
	newFilters.poiLocationLng = undefined;
	newFilters.locationSlug = undefined;

	const currentBounds = gmapRef.value?.mapInstance?.getBounds();
	const currentZoom = gmapRef.value?.mapInstance?.getZoom();

	if (currentBounds && currentZoom) {
		newFilters.bounds = getBoundsString(currentBounds);
		newFilters.zoom = currentZoom;
	}

	mapController.processSeeAllCity('filters-bar');

	const clusterFilters = {
		...newFilters,
		h3Ids: [],
		bounds: newFilters.bounds,
		zoom: newFilters.zoom
	};

	const regionsFilters = {
		regions: newFilters.regions,
		cities: newFilters.cities,
		states: newFilters.states,
		macroregions: newFilters.macroregions
	};

	clearFiltersAndClusters(newFilters, regionsFilters);
	mapController.forceUpdateClusters(clusterFilters as PropertyFilters);
}

function handleClearSelectedClusters() {
	trackSelectionCleared({
		current_page: 'Listing Map',
		property_count: totalSelectedPropertiesCount.value,
		square_count: selectedClustersData.value.size || 1
	});

	clearSelectedClusters();
}

async function handleClusterClicked(clusterData: BaseClusterData) {
	const cluster = clusters.value.find(
		c => c.geometry.id === clusterData.clusterId
	);
	if (!cluster) return;

	mapController.handleClusterNavigation();
	await propertyListSectionRef.value?.scrollToTop();
	toggleToHalfOpenWhenClosed();
	trackMapListingInteracted({
		current_page: 'Listing Map',
		click_squares: true,
		zoom_click: cluster.properties.pointCount > 20,
		final_click: cluster.properties.pointCount <= 20,
		square_count: selectedClustersData.value.size || 1,
		property_count: totalSelectedPropertiesCount.value
	});

	if (!gmapRef.value?.mapInstance) return;

	const idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
		google.maps.event.removeListener(idleListener);

		nextTick(() => {
			const bounds = gmapRef.value?.mapInstance?.getBounds();
			if (bounds) {
				mapController.updateMapBounds(bounds);
			}
		});
	});
}

function handleCardHover(h3ClusterIds: H3ClusterId[]) {
	if (selectedClustersData.value.size === 1) {
		return;
	}

	const clusterId = h3ClusterIds.find(
		x => x.zoom === gmapRef.value?.mapInstance?.getZoom()
	)?.id;

	const cluster = clusters.value.find(
		cluster => cluster.geometry.id === clusterId
	);

	if (cluster) {
		const markerRef = clusterMarkersRefs.value.get(cluster.geometry.id);
		markerRef?.triggerMarkerHover();
	}
}

function handleCardUnhover(h3ClusterIds: H3ClusterId[]) {
	if (selectedClustersData.value.size === 1) {
		return;
	}

	const clusterId = h3ClusterIds.find(
		x => x.zoom === gmapRef.value?.mapInstance?.getZoom()
	)?.id;

	const cluster = clusters.value.find(
		cluster => cluster.geometry.id === clusterId
	);

	if (cluster) {
		const markerRef = clusterMarkersRefs.value.get(cluster.geometry.id);
		markerRef?.triggerMarkerUnhover();
	}
}

function setClusterMarkerRef(
	clusterId: string,
	markerRef: InstanceType<typeof GmapClusterMarker> | null
) {
	if (markerRef && '$el' in markerRef) {
		clusterMarkersRefs.value.set(clusterId, markerRef);
	} else {
		clusterMarkersRefs.value.delete(clusterId);
	}
}

async function applyFiltersFromFilterLink(newFilters: PropertyFilters) {
	const hadSelectedClusters = selectedClustersData.value.size > 0;

	if (hadSelectedClusters) {
		clearSelectedClusters();
		await nextTick();
	}

	const baseFilters = { ...newFilters, bounds: undefined, zoom: undefined };
	const clusterFilters = { ...baseFilters, h3Ids: [] };
	const propertyFilters = { ...baseFilters, h3Ids: [] };

	updateClustersAndProperties(clusterFilters, propertyFilters, 'substitution');

	return baseFilters;
}

async function handleFilterLinkClicked(newFilters: PropertyFilters) {
	await applyFiltersFromFilterLink(newFilters);
}

const isViewMoreButtonInViewport = ref(false);
const viewMoreButtonPosition = ref<{ top: number } | undefined>();

const hasEmptyState = computed(() => !!emptyStateData.value);

const viewMoreButtonPositionComputation = computed(() => {
	if (hasEmptyState.value) return undefined;

	return viewMoreButtonPosition.value;
});

function handleViewMoreButtonVisibilityChanged(isVisible: boolean) {
	isViewMoreButtonInViewport.value = isVisible;
}

function handleViewMoreButtonPositionChanged(position: { top: number }) {
	viewMoreButtonPosition.value = position;
}

const totalSelectedPropertiesCount = computed(() => {
	let total = 0;
	for (const clusterId of selectedClustersData.value.keys()) {
		const cluster = clusters.value.find(c => c.geometry.id === clusterId);
		if (cluster) {
			total += cluster.properties.pointCount;
		}
	}
	return total;
});

function handleClusterSelectionChanged(params: {
	clusterId: string;
	isSelected: boolean;
}) {
	const cluster = clusters.value.find(c => c.geometry.id === params.clusterId);
	if (!cluster) return;

	originalHandleClusterSelectionChanged(params);
	nextTick(() => {
		trackMapListingInteracted({
			current_page: 'Listing Map',
			click_squares: true,
			zoom_click: false,
			final_click: true,
			square_count: selectedClustersData.value.size || 1,
			property_count: totalSelectedPropertiesCount.value
		});
	});
}

function handleClusterDeselectedByResolution(data: { clusterId: string }) {
	originalHandleClusterDeselectedByResolution(data);

	nextTick(() => {
		trackMapListingInteracted({
			current_page: 'Listing Map',
			click_squares: true,
			zoom_click: false,
			final_click: true,
			square_count: selectedClustersData.value.size || 1,
			property_count: totalSelectedPropertiesCount.value
		});
	});
}

function handleMenuBottomLevelClick() {
	if (isDragging.value) return;
	moveBackOneLevel();
}
const emptyStateRef = computed(() => {
	return propertyListSectionRef.value?.emptyStateRef;
});

const mobileScrollButtonHideAfterElement = computed(() => {
	return (emptyStateData.value && emptyStateRef.value?.$el) || null;
});

const headerDragOffsetY = useState<number>('header-drag-offset-y', () => 0);

const isHeaderHidden = computed(() => {
	return headerDragOffsetY.value >= HEADER_OFFSET_CONFIG.MAX_OFFSET;
});

const sectionHeightClass = computed(() => {
	if (isHeaderHidden.value) {
		return 'h-[calc(100dvh-81px)] lg:h-[calc(100dvh-163px)]';
	}

	return 'h-[calc(100dvh-141px)] lg:h-[calc(100dvh-163px)]';
});
</script>

<template>
	<ClientOnly>
		<template #fallback>
			<LazyFiltersFiltersBar
				:filters="filters"
				page="imoveis"
				:is-fetching-properties="isFetching"
				:count-properties="filteredCount || 0"
				class="h-[65px] lg:h-20.75"
			/>
		</template>

		<Teleport to="#filters-bar">
			<LazyFiltersFiltersBar
				v-model:is-sidebar-active="isSidebarActive"
				fixed
				:filters="filters"
				:applied-filter-keys="appliedFilterKeys"
				:number-of-applied-closed-filters="numberOfAppliedClosedFilters"
				:count-properties="filteredCount"
				:is-fetching-properties="isFetching"
				page="imoveis"
				class="h-[65px] lg:h-20.75"
				@update:filters="handleApplyFilter($event)"
				@filter-applied-for-code-search="handleSearchByCodeEvent"
				@filter-applied-for-condo="handleSearchByCondoEvent"
			/>
		</Teleport>
	</ClientOnly>

	<ClientOnly>
		<Teleport to="#filters-sidebar">
			<LazyFiltersFiltersSidebar
				v-model:is-active="isSidebarActive"
				:filters="filters"
				:filter-components="filterComponents"
				page-name="imoveis"
				:is-fetching-properties="isFetching"
				:count-properties="filteredCount"
				@apply-filters="handleSidebarApplyFilter"
				@clear-filters="handleSidebarClearFilters"
			/>

			<Transition
				:enter-active-class="'transition-opacity duration-300 ease-out'"
				:enter-from-class="'opacity-0'"
				:enter-to-class="'opacity-100'"
				:leave-active-class="'transition-opacity duration-300 ease-in'"
				:leave-from-class="'opacity-100'"
				:leave-to-class="'opacity-0'"
			>
				<div
					v-show="isSidebarActive"
					class="fixed inset-0 z-50 flex h-screen w-screen bg-black/40"
				/>
			</Transition>
		</Teleport>
	</ClientOnly>

	<div
		:class="{
			'pointer-events-none': isSidebarActive
		}"
		class="relative h-dvh w-full pt-[65px] lg:pt-[83px]"
	>
		<ClientOnly>
			<MobileScrollToMapButton
				v-if="propertyListSectionRef"
				:filters="filters"
				:is-menu-open="menuBottomLevel === 0"
				:scroll-element="propertyListSectionRef"
				:is-view-more-button-in-viewport="isViewMoreButtonInViewport"
				:view-more-button-position="viewMoreButtonPositionComputation"
				:hide-after="mobileScrollButtonHideAfterElement"
				@open-map-vision="handleOpenMapFromMobileButton"
			/>
		</ClientOnly>

		<div class="size-full divide-y divide-white-dark-1 bg-white">
			<div class="size-full bg-black">
				<LazyContainerFluid
					:main-class="[
						'grid grid-cols-1 lg:grid-cols-[minmax(0,45%)_1fr] h-full xl:grid-cols-[minmax(0,60%)_1fr] 2xl:grid-cols-[minmax(0,820px)_1fr]',
						shouldFixOnBottom ? 'overflow-hidden' : ''
					]"
					class="h-full bg-white"
				>
					<div
						ref="dragRef"
						class="z-20 h-[calc(100dvh-83px)] bg-white pt-4 lg:cursor-auto! lg:pt-0"
					>
						<div
							id="drawer"
							class="absolute top-0 flex h-[54px] w-full justify-center lg:hidden"
						>
							<div class="mt-[6px] h-1 w-12 rounded-full bg-white-dark-3" />
						</div>

						<LazyPropertyListSection
							ref="propertyListSectionRef"
							:class="[isMenuOpen ? '' : 'h-0! opacity-0', sectionHeightClass]"
							:is-fixed-bottom="shouldFixOnBottom"
							:is-user-dragging="isDragging"
							:is-fetching-for-the-first-time="isFetchingForTheFirstTime"
							:has-properties="hasProperties"
							:property-count-label="propertyCountLabel"
							:info-label="infoLabel"
							:filters="filters"
							:is-fetching="isFetching"
							:properties="properties"
							:should-show-next-page-button="shouldShowNextPageButton"
							:empty-state-data="emptyStateData"
							:breadcrumb-items="breadcrumbItems"
							:menu-bottom-level="menuBottomLevel"
							:current-y="currentY"
							:is-dragging="isDragging"
							:is-transitioning="isTransitioning"
							@update-ordenation="updateOrdenation"
							@reorder-clicked="handleReorderClicked"
							@next-page="nextPage"
							@breadcrumb-clicked="handleBreadcrumbClicked"
							@region-clicked="handleRegionClicked"
							@property-card-hover="handleCardHover"
							@property-card-unhover="handleCardUnhover"
							@filter-link-clicked="handleFilterLinkClicked"
							@view-more-button-visibility-changed="
								handleViewMoreButtonVisibilityChanged
							"
							@view-more-button-position-changed="
								handleViewMoreButtonPositionChanged
							"
						/>
						<div
							v-show="shouldShowMobilePropertyCountLabel"
							class="flex h-[500px] w-full items-start justify-start bg-white px-4 text-primary"
							@click="handleMenuBottomLevelClick"
						>
							<div class="flex items-center">
								<IconMaterialSymbolsLightChevronLeft
									class="mr-2 size-6 rotate-90"
								/>
								<span class="text-sm font-medium">
									{{ mobilePropertyCountLabel }}
								</span>
							</div>
						</div>

						<div
							v-show="!shouldShowMobilePropertyCountLabel"
							class="flex h-[500px] w-full items-start justify-start bg-white px-4 text-primary"
						>
							<div class="flex w-full items-center gap-5">
								<Skeleton is-active variant="white" class="size-[28px]" />
								<Skeleton is-active variant="white" class="h-5 flex-1" />
							</div>
						</div>
					</div>

					<section
						id="map-section"
						class="absolute h-[calc(100dvh-121px)] w-full lg:relative lg:h-[calc(100dvh-163px)]"
					>
						<ClientOnly fallback-tag="div">
							<template #fallback>
								<div
									class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
								></div>
							</template>
							<div
								class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
							>
								<GmapMap
									v-show="shouldShowMap"
									id="portal-listing-map"
									ref="gmapRef"
									map-id="'portal-listing-map'"
									:center="mapState.mapConfig.center"
									:bounds="mapState.mapConfig.bounds"
									:zoom="mapState.mapConfig.zoom"
									:min-zoom="mapState.mapConfig.minZoom"
									:max-zoom="mapState.mapConfig.maxZoom"
									:options="mapOptions"
									:clickable-icons="false"
									class="relative size-full h-full! touch-none"
									@drag-end="handleDragEnd"
									@zoom-changed="handleZoomChanged"
								>
									<GmapClusterMarker
										v-for="cluster in readyClusters"
										:key="getClusterKey(cluster.geometry.id)"
										:ref="(el: Element | ComponentPublicInstance | null) => setClusterMarkerRef(cluster.geometry.id, el as InstanceType<typeof GmapClusterMarker> | null)"
										:min-zoom="mapState.mapConfig.minZoom"
										:max-zoom="mapState.mapConfig.maxZoom"
										:cluster="cluster"
										:map="gmapRef?.mapInstance"
										:selected-from-parent="
											selectedClustersData.has(cluster.geometry.id)
										"
										:cluster-sizes="
											mapController.clusterSizes.value.get(cluster.geometry.id)
										"
										:selected-clusters-count="selectedClustersData.size"
										@cluster-selection-changed="handleClusterSelectionChanged"
										@cluster-deselected-by-resolution-change="
											handleClusterDeselectedByResolution
										"
										@cluster-pan-zoom="handleClusterClicked"
										@clear-selected-clusters-before-zoom="
											handleClearSelectedClusters
										"
									/>
									<GmapMarker
										v-if="markerPosition"
										:position="markerPosition"
										:variant="'red'"
										size="default"
										:address="addressGmapMarker"
										:gmp-clickable="false"
										:gmp-draggable="false"
									/>

									<template #visible>
										<div
											class="absolute right-4 top-4 flex items-center gap-4 md:right-6 md:top-6 [&_button]:px-4 [&_button]:py-2"
										>
											<Button
												v-if="shouldShowSeeAllCityButton"
												variant="white-shadow"
												class="size-min h-10 md:h-11"
												size="sm"
												@click="handleSeeAllCity"
											>
												Ver toda a cidade
											</Button>
											<Button
												v-if="selectedClustersData.size > 0"
												variant="primary"
												class="size-min h-10 md:h-11"
												size="sm"
												@click="handleClearSelectedClusters"
											>
												Limpar seleção
												<span class="size-5 rounded-full bg-primary-light-3">
													{{ selectedClustersData.size }}
												</span>
											</Button>
										</div>
									</template>
								</GmapMap>
							</div>
						</ClientOnly>
					</section>
				</LazyContainerFluid>
			</div>
		</div>
	</div>
</template>
